/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Oct 09 2024
 **/

import produce from "immer";
import { has } from "lodash-es";

export function isOpenWorld(space: V2.SpaceInfo): space is V2.OpenWorldSpaceInfo {
	return (space as V2.OpenWorldSpaceInfo).spaceType === "OPEN_WORLD";
}
export function isGroupSpace(space: V2.SpaceInfo): space is V2.GroupSpaceInfo {
	return (space as V2.GroupSpaceInfo).spaceType === "NORMAL" || (space as V2.GroupSpaceInfo).spaceType === "DEFAULT";
}
export function isMyRoom(space: V2.SpaceInfo): space is V2.MyRoomSpaceInfo {
	return has<V2.MyRoomSpaceInfo>(space as V2.MyRoomSpaceInfo, "useGuestBook");
}
export function hasSpaceDetailInfo(space: V2.SpaceInfo) {
	return has(space?.timestamp, "timestamp");
}

export function formatfetchedMyRoom(res: WebServices.Space.MySpaces.List.Data["myRoom"]): V2.MyRoomSpaceInfo | null {
	if (!res) {
		return null;
	} else {
		return {
			feSpaceType: "MY_ROOM",
			participantUserIDs: res.participantUsers.map((user) => user.id),
			spaceId: res.id || "",
			spaceName: res.name || "",
			timestamp: Date.now(),
			updatedMethod: "restAPI",
			...res
		};
	}
}

export function formatfetchedSpaceDetail(res: WebServices.Space.Detail.Data | undefined): V2.SpaceInfo | null {
	if (!res) {
		return null;
	} else {
		if (res.spaceType === "OPEN_WORLD" || res.spaceType === "GROUP_OPEN_WORLD") {
			return {
				feSpaceType: "OPEN_WORLD",
				participantUserIDs: res.participantUsers?.map((user) => user.id) || [],
				spaceName: res.name,
				timestamp: Date.now(),
				updatedMethod: "restAPI",
				...res
			};
		} else {
			return {
				feSpaceType: "GROUP_SPACE",
				participantUserIDs: res.participantUsers?.map((user) => user.id) || [],
				spaceName: res.name,
				timestamp: Date.now(),
				updatedMethod: "restAPI",
				...res
			};
		}
	}
}

export function formatfetchedOpenWorld(res: WebServices.Space.Detail.Data | undefined): V2.OpenWorldSpaceInfo | null {
	if (!res) {
		return null;
	} else {
		return {
			feSpaceType: "OPEN_WORLD",
			participantUserIDs: [],
			spaceName: res.name,
			timestamp: Date.now(),
			updatedMethod: "restAPI",
			...res
		};
	}
}

export function formatfetchedGroupSpace(
	res: WebServices.Space.MySpaces.List.Data["groups"][number]["spaces"][number],
	groupID: GROUP_ID
): V2.GroupSpaceInfo | null {
	if (!res) {
		return null;
	} else {
		return {
			feSpaceType: "GROUP_SPACE",
			participantUserIDs: [],
			groupId: groupID,
			timestamp: Date.now(),
			updatedMethod: "restAPI",
			...res
		};
	}
}

export function formatExternalGroupSpace(
	res: WebServices.Space.MySpaces.List.Data["externalGroup"][number]
): V2.GroupSpaceInfo | null {
	if (!res) {
		return null;
	} else {
		return {
			feSpaceType: "GROUP_SPACE",
			participantUserIDs: [],
			timestamp: Date.now(),
			updatedMethod: "restAPI",
			...res
		};
	}
}
export function validate_space_name(spaceName: string, info: Partial<V2.SpaceInfo>): Partial<V2.SpaceInfo> {
	return produce(info, (draft) => {
		if (spaceName !== draft.name) draft.name = spaceName;
		if (spaceName !== draft.spaceName) draft.spaceName = spaceName;
	});
}
